/**
 * Close Shift Dialog - End of Shift Report
 * Generates summary of all activities during the shift
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { 
  ClipboardCheck, 
  LogIn, 
  LogOut, 
  UserPlus, 
  Calendar, 
  Clock,
  CheckCircle,
  Download,
  Send
} from 'lucide-react';
import { BACKEND_URL } from '@/lib/backendUrl';


export function CloseShiftDialog({ 
  open, 
  onOpenChange, 
  hotelId, 
  staffEmail,
  staffName,
  shiftActivities 
}) {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [shiftStart] = useState(new Date());
  
  const calculateShiftDuration = () => {
    const now = new Date();
    const diff = now - shiftStart;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const handleCloseShift = async () => {
    try {
      setLoading(true);
      
      const shiftReport = {
        hotel_id: hotelId,
        staff_email: staffEmail || 'unknown@hotel.com',
        staff_name: staffName || 'Front Desk Staff',
        shift_start: shiftStart.toISOString(),
        shift_end: new Date().toISOString(),
        duration: calculateShiftDuration(),
        activities: {
          check_ins: shiftActivities.checkIns || [],
          check_outs: shiftActivities.checkOuts || [],
          walk_ins: shiftActivities.walkIns || [],
          bookings: shiftActivities.bookings || []
        },
        summary: {
          total_check_ins: (shiftActivities.checkIns || []).length,
          total_check_outs: (shiftActivities.checkOuts || []).length,
          total_walk_ins: (shiftActivities.walkIns || []).length,
          total_bookings: (shiftActivities.bookings || []).length
        },
        notes: notes,
        created_at: new Date().toISOString()
      };

      console.log('🔍 SHIFT REPORT - Sending data:', JSON.stringify(shiftReport, null, 2));
      console.log('🔍 staffEmail:', staffEmail, 'staffName:', staffName);

      const response = await fetch(`${BACKEND_URL}/api/pms/shift-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(shiftReport)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Shift closed successfully! 🎉');
        onOpenChange(false);
        
        // Reset activities for next shift
        if (window.resetShiftActivities) {
          window.resetShiftActivities();
        }
      } else {
        throw new Error(data.error || 'Failed to close shift');
      }
    } catch (error) {
      console.error('Error closing shift:', error);
      toast.error('Failed to close shift');
    } finally {
      setLoading(false);
    }
  };

  const activities = shiftActivities || {};
  const checkIns = activities.checkIns || [];
  const checkOuts = activities.checkOuts || [];
  const walkIns = activities.walkIns || [];
  const bookings = activities.bookings || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-3 rounded-xl">
              <ClipboardCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold text-white mb-1">
                Close Out Shift
              </DialogTitle>
              <p className="text-white/90 text-sm">
                Review your shift activities and complete close-out
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Shift Info */}
          <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-100">
            <CardContent className="p-5">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <Clock className="w-5 h-5 text-indigo-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Shift Duration</p>
                  <p className="font-bold text-lg text-gray-900">{calculateShiftDuration()}</p>
                </div>
                <div>
                  <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Staff Member</p>
                  <p className="font-bold text-lg text-gray-900">{staffName}</p>
                </div>
                <div>
                  <Calendar className="w-5 h-5 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Date</p>
                  <p className="font-bold text-lg text-gray-900">
                    {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Activity Summary */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-indigo-600" />
              Shift Activity Summary
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {/* Check-Ins */}
              <Card className="border-2 border-green-200 bg-green-50">
                <CardContent className="p-4 text-center">
                  <LogIn className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-green-600">{checkIns.length}</p>
                  <p className="text-sm text-gray-700 font-medium">Check-Ins</p>
                </CardContent>
              </Card>

              {/* Check-Outs */}
              <Card className="border-2 border-orange-200 bg-orange-50">
                <CardContent className="p-4 text-center">
                  <LogOut className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-orange-600">{checkOuts.length}</p>
                  <p className="text-sm text-gray-700 font-medium">Check-Outs</p>
                </CardContent>
              </Card>

              {/* Walk-Ins */}
              <Card className="border-2 border-blue-200 bg-blue-50">
                <CardContent className="p-4 text-center">
                  <UserPlus className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-blue-600">{walkIns.length}</p>
                  <p className="text-sm text-gray-700 font-medium">Walk-Ins</p>
                </CardContent>
              </Card>

              {/* Bookings */}
              <Card className="border-2 border-purple-200 bg-purple-50">
                <CardContent className="p-4 text-center">
                  <Calendar className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-purple-600">{bookings.length}</p>
                  <p className="text-sm text-gray-700 font-medium">Bookings</p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Lists */}
            <div className="space-y-4">
              {checkIns.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Guests Checked In ({checkIns.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {checkIns.map((guest, idx) => (
                      <Badge key={idx} className="bg-green-100 text-green-800 border-green-300">
                        {guest.guest_name} - Room {guest.room_number}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {checkOuts.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                    <LogOut className="w-4 h-4" />
                    Guests Checked Out ({checkOuts.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {checkOuts.map((guest, idx) => (
                      <Badge key={idx} className="bg-orange-100 text-orange-800 border-orange-300">
                        {guest.guest_name} - Room {guest.room_number}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {walkIns.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Walk-In Guests ({walkIns.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {walkIns.map((guest, idx) => (
                      <Badge key={idx} className="bg-blue-100 text-blue-800 border-blue-300">
                        {guest.guest_name} - Room {guest.room_number}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {bookings.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Bookings Created ({bookings.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {bookings.map((booking, idx) => (
                      <Badge key={idx} className="bg-purple-100 text-purple-800 border-purple-300">
                        {booking.guest_name} - {booking.room_type}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* No Activity Message */}
              {checkIns.length === 0 && checkOuts.length === 0 && 
               walkIns.length === 0 && bookings.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <p className="text-gray-500 font-medium">No activities recorded during this shift</p>
                  <p className="text-sm text-gray-400 mt-1">Your shift report will be saved with zero activities</p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Shift Notes */}
          <div className="space-y-3">
            <Label className="text-base font-bold text-gray-900">
              Shift Notes (Optional)
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about incidents, issues, or important information for the next shift..."
              rows={4}
              className="text-sm"
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              disabled={loading}
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
            <Button 
              onClick={handleCloseShift}
              disabled={loading}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold shadow-lg px-6"
            >
              {loading ? (
                <>Processing...</>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit & Close Shift
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
